import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { TranslatePage } from './pages/TranslatePage';
import NotFoundPage from './pages/NotFoundPage';
import { LoginPage } from './pages/LoginPage'; // Import Login Page
import { SignupPage } from './pages/SignupPage'; // Import Signup Page
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from '@/components/themes/ThemeProvider';
import ProtectedRoute from '@/components/auth/ProtectedRoute'; // Future
import { HomePage } from './pages/HomePage';
import { CheckoutStatusPage } from './pages/CheckoutStatusPage';
import { ErrorPage } from './pages/ErrorPage';
import { GuestLoginPage } from './pages/GuestLoginPage';
import LogoutPage from './pages/LogoutPage';
import { useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { ClientConfig } from '../shared/types';
import { loadMatchingClientConfig } from '../shared/configLoader';
import { ClientStyle } from './CustomStyle';
import { useAppStore } from './hooks/useAppStore';
import { getBrowserInfo } from './lib/browser';

import { WrongBrowserDialog } from './components/dialogs/WrongBrowserDialog';
import NewRoomPage from './pages/NewRoom';
import EventPage, { findEventFromId } from './pages/EventPage';
import AdminChatPanel from './pages/admin/AdminTranslatePage';
import AdminRoute from './components/auth/AdminRoute';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import { AdminAuthCallbackPage } from './pages/admin/AdminAuthCallbackPage';
import TutorialOverlay from './components/tutorial/TutorialOverlay';
import { getChottoAuthToken } from './services/chotto';
import { storeAuthToken, getStoredAuthToken } from './lib/auth';

// Lazy load ThreeJS background for better initial load performance
const ThreeJsBackground = lazy(() => import('./components/background/ThreeJsBackground'));

import AdminPlaygroundPage from './pages/admin/AdminPlaygroundPage';
import { ErrorBoundary } from './components/error/ErrorBoundary';


const router = createBrowserRouter([
  {
    path: "/",
    element: <HomePage />,
  },
  {
    element: <ProtectedRoute />, // Protect chat
    children: [{
      path: "/translate",
      element: <TranslatePage />
    }]
  },
  {
    element: <ProtectedRoute />, // Protect chat
    children: [{
      path: "/new-room",
      element: <NewRoomPage />
    }]
  },
  {
    path: "/evt",
    element: <EventPage />
  },
  {
    path: "/evt-qr",
    element: <EventPage />
  },
  // {
  //   path: "/translate", // Unprotected chat
  //   element: <TranslatePage />
  // },
  {
    path: "/auth/:token",
    element: <AuthCallbackPage />,
  },
  {
    path: "/admin/auth/:token",
    element: <AdminAuthCallbackPage />,
  },
  { // Add Login Route
    path: "/login",
    element: <LoginPage />,
  },
  { // Add Signup Route
    path: "/signup",
    element: <SignupPage />,
  },
  { // Logout Route
    path: "/logout",
    element: <LogoutPage />,
  },
  {
    path: "/checkout",
    element: <CheckoutStatusPage />,
  },
  {
    path: "/guest-login",
    element: <GuestLoginPage />,
  },
  {
    path: "/error",
    element: <ErrorPage />,
  },
  {
    path: "/playground",
    element: <AdminPlaygroundPage />,
  },
  {
    element: <AdminRoute />, // Protect chat
    children: [{
      path: "/admin",
      element: <AdminChatPanel />
    }, {
      path: "/admin/playground",
      element: <AdminPlaygroundPage />
    }]
  },
  {
    path: "/admin-login",
    element: <AdminLoginPage />
  },
  {
    path: "*", // Catch-all
    element: <NotFoundPage />,

  },
].map(route => {
  return {
    ...route,
    ErrorBoundary
  }
}));

// --- Authentication State Provider (Example using Context) ---
// You might wrap RouterProvider with your AuthContext Provider
// import { AuthProvider } from './contexts/AuthContext';
// function App() {
//   return (
//     <AuthProvider> {/* Wrap with Auth Provider */}
//       <RouterProvider router={router} />
//       <Toaster position="top-center" richColors />
//     </AuthProvider>
//   );
// }
// --- Or integrate auth into your Zustand store ---

function App() {
  const setClientConfig = useAppStore((s) => s.setClientConfig);
  const isPresentationMode = useAppStore((s) => s.isPresentationMode);
  const setUser = useAppStore((s) => s.setUser);
  const searchParams = new URLSearchParams(window.location.search)
  const roomId = searchParams.get('r') || '';
  //get event id
  const isEventPage = window.location.pathname == '/evt';
  const eventId = searchParams.get('id') || '';
  const host = window.location.hostname;
  const subDomain = host.split('.')[0];

  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    const checkBrowser = async () => {
      const { isInApp, isInChottoApp } = await getBrowserInfo();
      if (isInApp && !isInChottoApp) {
        console.error("App validation failed: Not in Chotto App");
        setIsBlocked(true);
      }
    };
    checkBrowser();
  }, []);

  const config: ClientConfig | null = useMemo(
    () => {
      let roomPrefix = null;
      if (isEventPage) {
        //override roomId from event
        roomPrefix = findEventFromId(eventId);
      }
      return loadMatchingClientConfig({ subDomain, roomId: roomPrefix || roomId, cname: host });
    },
    [subDomain, roomId, host]
  );
  //set immediately to prevent flickering
  config && setClientConfig(config);

  useEffect(() => {
    config && setClientConfig(config);
  }, [config]);

  // Tutorial state
  const [showTutorial, setShowTutorial] = useState(false);
  const [appReady, setAppReady] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    // Skip tutorial for non-Chotto clients
    if (config?.config.name !== 'CHOTTO') {
      setAppReady(true);
      return;
    }

    const tutorialCompleted = localStorage.getItem('chotto_tutorial_completed');
    if (!tutorialCompleted) {
      // Check URL and update if necessary
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search;
      const hasRoomId = currentSearch.includes('r=');

      if (currentPath !== '/translate' || !hasRoomId) {
        setIsRedirecting(true);
        window.location.href = '/translate?r=new';
        return;
      }

      setShowTutorial(true);
    } else {
      setAppReady(true);
    }
  }, [config]);

  // Attempt background login on mount to prepare for connection
  useEffect(() => {
    const initBackgroundSession = async () => {
      // Only for CHOTTO client and if no user exists
      if (config?.config.name === 'CHOTTO' && !getStoredAuthToken().user) {
        try {
          console.log('Starting background guest login...');
          const token = await getChottoAuthToken();
          const user = storeAuthToken(token);
          setUser(user);
          console.log('Background guest login successful');
        } catch (e) {
          console.error('Background guest login failed:', e);
        }
      }
    };
    
    initBackgroundSession();
  }, [config]);

  // Signal native iOS app that React app is ready (runs once on mount)
  // This dismisses the native splash so user can see/interact with tutorial
  useEffect(() => {
    const timer = setTimeout(() => {
      // Signal to native iOS that the app is ready via global variable
      // The native SplashViewController polls for this
      (window as any).__nativeAppReady = true;
      console.log('Native app ready signal sent');
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleTutorialComplete = async () => {
    localStorage.setItem('chotto_tutorial_completed', 'true');
    setShowTutorial(false);

    // Only do login if we don't have a user yet
    // This preserves the identity created during background/mic-enable phase
    const currentUser = getStoredAuthToken().user;
    if (!currentUser) {
      try {
        const token = await getChottoAuthToken();
        const user = storeAuthToken(token);
        setUser(user);
      } catch (e) {
        console.error('Failed to auto-login after tutorial:', e);
        // Will fall back to GuestLoginPage via ProtectedRoute
      }
    }

    setAppReady(true);
  };

  const handleMicrophoneEnabled = () => {
    // Start loading the app in the background once mic is enabled
    setAppReady(true);
  };

  // Block screen if in embedded webview
  if (isBlocked) {
    return <WrongBrowserDialog url={window.location.href} />;
  }

  return (
    <>
      {appReady && (
        <>
          {/* ThreeJS animated background - Only for Chotto skin */}
          {config?.config.name === 'CHOTTO' && !isPresentationMode && (
            <Suspense fallback={null}>
              <ThreeJsBackground />
            </Suspense>
          )}
          <ThemeProvider defaultTheme="system" storageKey="ui-theme">
            <ClientStyle />
            <RouterProvider router={router} />
            <Toaster position="top-center" richColors closeButton mobileOffset={100} gap={0} visibleToasts={2} />
          </ThemeProvider>
        </>
      )}
      {(showTutorial || isRedirecting) && (
        <TutorialOverlay
          onComplete={handleTutorialComplete}
          onMicrophoneEnabled={handleMicrophoneEnabled}
          isRedirecting={isRedirecting}
        />
      )}
    </>
  );
}

export default App;
